import { Exchange } from '@/types/generated/common/types'

/**
 * Ensures every order-form selection item has a valid account while preserving
 * explicit account-scoped rows. `resolveAccountId` receives the original item,
 * so callers can retain a valid injected account and use the exchange fallback
 * only for rows that arrived without one. Pure/side-effect-free so
 * the caller (use-unified-order-form.ts) decides whether/when to write the
 * result back (only when `changed`, to avoid a render loop).
 *
 * Dedupe policy when two-or-more items land on the same `getGroupKey`: prefer
 * the item whose ORIGINAL accountId already equaled the target account (its
 * amount/side is that account's real data) over one merely rewritten onto it;
 * otherwise keep the first item in array order. `resolveLiveOverride`, when
 * given, is applied to each item using its ORIGINAL (pre-rewrite) identity —
 * letting the caller thread in a live value (e.g. an in-progress React Hook
 * Form amount) that the item's own possibly-stale field wouldn't reflect —
 * before the dedupe/rewrite decision is made.
 */
export function normalizeAccountScopedItems<T extends { accountId?: string }>(
  items: T[],
  getGroupKey: (item: T) => string,
  getExchange: (item: T) => Exchange,
  resolveAccountId: (item: T, exchange: Exchange) => string | undefined,
  resolveLiveOverride?: (item: T, exchange: Exchange) => Partial<T> | undefined
): { items: T[]; changed: boolean } {
  let changed = false
  const kept = new Map<string, { item: T; matchedOriginal: boolean }>()
  const order: string[] = []

  for (const original of items) {
    const exchange = getExchange(original)
    const target = resolveAccountId(original, exchange)

    const override = resolveLiveOverride?.(original, exchange)
    const overrideDiffers =
      !!override &&
      (Object.keys(override) as (keyof T)[]).some((key) => override[key] !== original[key])
    const withOverride = overrideDiffers ? { ...original, ...override } : original
    if (withOverride !== original) changed = true

    if (target === undefined) {
      const key = getGroupKey(withOverride)
      if (!kept.has(key)) {
        kept.set(key, { item: withOverride, matchedOriginal: true })
        order.push(key)
      } else {
        changed = true
      }
      continue
    }

    const matchedOriginal = original.accountId === target
    const normalized =
      withOverride.accountId === target ? withOverride : { ...withOverride, accountId: target }
    if (normalized !== original) changed = true

    const key = getGroupKey(normalized)
    const existing = kept.get(key)
    if (!existing) {
      kept.set(key, { item: normalized, matchedOriginal })
      order.push(key)
    } else if (!existing.matchedOriginal && matchedOriginal) {
      kept.set(key, { item: normalized, matchedOriginal })
      changed = true
    } else {
      changed = true
    }
  }

  if (!changed) return { items, changed: false }
  const normalizedItems: T[] = []
  for (const key of order) {
    const entry = kept.get(key)
    if (entry) normalizedItems.push(entry.item)
  }
  return { items: normalizedItems, changed: true }
}
